// Co-living catalog lists: roles and issue categories. Both endpoints return
// plain arrays (no paging), so the store holds the whole list and the pages
// filter it. Every write refetches so counts (`member_count`, `routed`) stay
// right.
import {
  listRoles,
  createRole,
  updateRole,
  deleteRole,
} from '../../api/coLiving/roles';
import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../../api/coLiving/categories';

export const state = {
  roles: [],
  categories: [],
  uiFlags: {
    isFetchingRoles: false,
    isFetchingCategories: false,
  },
};

export const getters = {
  getRoles: s => s.roles,
  getActiveRoles: s => s.roles.filter(r => r.active),
  getCategories: s => s.categories,
  getActiveCategories: s => s.categories.filter(c => c.active),
  getUiFlags: s => s.uiFlags,
};

export const mutations = {
  setRoles(s, roles) {
    s.roles = roles;
  },
  setCategories(s, categories) {
    s.categories = categories;
  },
  setUiFlag(s, { key, value }) {
    s.uiFlags = { ...s.uiFlags, [key]: value };
  },
};

async function withFlag(commit, key, task) {
  commit('setUiFlag', { key, value: true });
  try {
    return await task();
  } finally {
    commit('setUiFlag', { key, value: false });
  }
}

export const actions = {
  // Always the full list (`status=all`): the page filters by status itself,
  // and routing dropdowns need the active ones from the same copy.
  fetchRoles({ commit }) {
    return withFlag(commit, 'isFetchingRoles', async () => {
      const { data } = await listRoles({ status: 'all' });
      commit('setRoles', data);
      return data;
    });
  },
  async addRole({ dispatch }, fields) {
    const { data } = await createRole(fields);
    await dispatch('fetchRoles');
    return data;
  },
  async editRole({ dispatch }, { key, fields }) {
    const { data } = await updateRole(key, fields);
    await dispatch('fetchRoles');
    return data;
  },
  async removeRole({ dispatch }, key) {
    await deleteRole(key);
    await dispatch('fetchRoles');
  },
  fetchCategories({ commit }) {
    return withFlag(commit, 'isFetchingCategories', async () => {
      const { data } = await listCategories({ status: 'all' });
      commit('setCategories', data);
      return data;
    });
  },
  async addCategory({ dispatch }, fields) {
    const { data } = await createCategory(fields);
    await dispatch('fetchCategories');
    return data;
  },
  async editCategory({ dispatch }, { key, fields }) {
    const { data } = await updateCategory(key, fields);
    await dispatch('fetchCategories');
    return data;
  },
  async removeCategory({ dispatch }, key) {
    await deleteCategory(key);
    await dispatch('fetchCategories');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
